import React, { useState, useEffect } from 'react';
import {
  FileText,
  Download,
  Eye,
  Upload as UploadIcon,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { DocumentUpload, DocumentType } from '../../components/DocumentUpload';
import { customerApi } from '../../api';
import { BASE_URL } from '../../api/client';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';

interface Document {
  document_id: string;
  document_type: string;
  file_name: string;
  file_url: string;
  file_size: number;
  uploaded_at: string;
  verified: boolean;
  verified_at?: string;
  verified_by?: string;
  status: 'pending' | 'verified' | 'rejected';
  rejection_reason?: string;
}

const INDIVIDUAL_DOCUMENT_TYPES: DocumentType[] = [
  {
    id: 'selfie',
    label: 'Selfie / Photo ID',
    required: true,
    description: 'Take a selfie with your device camera for identity verification',
    acceptedFormats: 'JPG, PNG',
    isSelfie: true,
  },
  {
    id: 'national_id',
    label: 'Government-Issued ID',
    required: true,
    description: 'National ID (NIN), Driver\'s License, or International Passport',
    acceptedFormats: 'PDF, JPG, PNG',
  },
  {
    id: 'salary_slip',
    label: 'Proof of Income',
    required: true,
    description: 'Salary slip or employment letter',
    acceptedFormats: 'PDF, JPG, PNG',
  },
  {
    id: 'bank_statement',
    label: 'Bank Statements',
    required: true,
    description: '3-6 months bank statements',
    acceptedFormats: 'PDF',
  },
  {
    id: 'utility_bill',
    label: 'Proof of Address',
    required: true,
    description: 'Utility bill (NEPA, water, etc.)',
    acceptedFormats: 'PDF, JPG, PNG',
  },
  {
    id: 'passport',
    label: 'Passport Photograph',
    required: true,
    description: 'Recent passport-sized photograph',
    acceptedFormats: 'JPG, PNG',
  },
];

const BUSINESS_DOCUMENT_TYPES: DocumentType[] = [
  ...INDIVIDUAL_DOCUMENT_TYPES,
  {
    id: 'cac_certificate',
    label: 'Business Registration (CAC)',
    required: true,
    description: 'Certificate of Incorporation from CAC',
    acceptedFormats: 'PDF',
  },
  {
    id: 'tax_clearance',
    label: 'Tax Clearance Certificate',
    required: true,
    description: 'Valid tax clearance certificate',
    acceptedFormats: 'PDF',
  },
  {
    id: 'bank_statement',
    label: 'Business Bank Statements',
    required: true,
    description: '6-12 months business bank statements',
    acceptedFormats: 'PDF',
  },
  {
    id: 'utility_bill',
    label: 'Business Address Verification',
    required: false,
    description: 'Utility bill or lease agreement for business premises',
    acceptedFormats: 'PDF, JPG, PNG',
  },
  {
    id: 'business_plan',
    label: 'Business Plan / Financial Statements',
    required: false,
    description: 'Business plan or audited financial statements (if available)',
    acceptedFormats: 'PDF',
  },
];

export const Documents: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [accountType, setAccountType] = useState<'individual' | 'business'>('individual');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await customerApi.getDocuments();
      if (response.success && response.data) {
        setDocuments(response.data.documents || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (uploadedDocuments: { type: string; file: File }[]) => {
    const formData = new FormData();

    uploadedDocuments.forEach((doc) => {
      formData.append('files', doc.file);
      formData.append('document_types', doc.type);
    });

    const response = await customerApi.uploadDocuments(formData);

    if (response.success) {
      setShowUploadForm(false);
      fetchDocuments();
    } else {
      throw new Error(response.message || 'Upload failed');
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const response = await customerApi.deleteDocument(documentId);
      if (response.success) {
        fetchDocuments();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete document');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle size={20} className="status-icon verified" />;
      case 'rejected':
        return <XCircle size={20} className="status-icon rejected" />;
      default:
        return <Clock size={20} className="status-icon pending" />;
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    const allTypes = [...INDIVIDUAL_DOCUMENT_TYPES, ...BUSINESS_DOCUMENT_TYPES];
    const docType = allTypes.find((dt) => dt.id === type);
    return docType?.label || type;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="documents-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>My Documents</h1>
          <p>Manage and upload your verification documents</p>
        </div>
        <Button onClick={() => setShowUploadForm(!showUploadForm)} variant="primary">
          <UploadIcon size={20} />
          {showUploadForm ? 'Cancel Upload' : 'Upload Documents'}
        </Button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      {showUploadForm && (
        <div className="upload-form-section">
          <div className="account-type-selector">
            <label>Account Type:</label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  value="individual"
                  checked={accountType === 'individual'}
                  onChange={(e) => setAccountType(e.target.value as 'individual')}
                />
                <span>Individual/Personal</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  value="business"
                  checked={accountType === 'business'}
                  onChange={(e) => setAccountType(e.target.value as 'business')}
                />
                <span>Business</span>
              </label>
            </div>
          </div>

          <DocumentUpload
            documentTypes={
              accountType === 'business' ? BUSINESS_DOCUMENT_TYPES : INDIVIDUAL_DOCUMENT_TYPES
            }
            onUpload={handleUpload}
          />
        </div>
      )}

      <div className="documents-list-section">
        <h2>Uploaded Documents</h2>

        {documents.length === 0 ? (
          <div className="empty-state">
            <FileText size={64} />
            <h3>No documents uploaded yet</h3>
            <p>Upload your verification documents to complete your profile and apply for loans.</p>
            <Button onClick={() => setShowUploadForm(true)} variant="primary">
              <UploadIcon size={20} />
              Upload Documents
            </Button>
          </div>
        ) : (
          <div className="documents-grid">
            {documents.map((doc) => (
              <div key={doc.document_id} className="document-card">
                <div className="document-header">
                  <div className="document-icon">
                    <FileText size={24} />
                  </div>
                  <div className="document-status">{getStatusIcon(doc.status)}</div>
                </div>

                <div className="document-body">
                  <h3 className="document-type">{getDocumentTypeLabel(doc.document_type)}</h3>
                  <p className="document-filename">{doc.file_name}</p>
                  <div className="document-meta">
                    <span className="file-size">{formatFileSize(doc.file_size)}</span>
                    <span className="upload-date">
                      {new Date(doc.uploaded_at).toLocaleDateString()}
                    </span>
                  </div>

                  {doc.status === 'verified' && doc.verified_at && (
                    <div className="verification-info">
                      <CheckCircle size={16} />
                      <span>Verified on {new Date(doc.verified_at).toLocaleDateString()}</span>
                    </div>
                  )}

                  {doc.status === 'rejected' && doc.rejection_reason && (
                    <div className="rejection-info">
                      <AlertCircle size={16} />
                      <span>{doc.rejection_reason}</span>
                    </div>
                  )}
                </div>

                <div className="document-actions">
                  <Button
                    onClick={() => window.open(`${BASE_URL}/${doc.file_url}`, '_blank')}
                    variant="secondary"
                    size="sm"
                    icon={<Eye size={16} />}
                  >
                    View
                  </Button>
                  <Button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `${BASE_URL}/${doc.file_url}`;
                      link.download = doc.file_name;
                      link.click();
                    }}
                    variant="secondary"
                    size="sm"
                    icon={<Download size={16} />}
                  >
                    Download
                  </Button>
                  {doc.status === 'pending' && (
                    <Button
                      onClick={() => handleDelete(doc.document_id)}
                      variant="danger"
                      size="sm"
                      icon={<Trash2 size={16} />}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="document-requirements-section">
        <h2>Document Requirements</h2>
        <div className="requirements-grid">
          <div className="requirement-card">
            <h3>Individual/Personal Loans</h3>
            <ul>
              <li>Government-issued ID (National ID, Driver's License, or Passport)</li>
              <li>Proof of income (salary slip or employment letter)</li>
              <li>Bank statements (3-6 months)</li>
              <li>Utility bill (proof of address)</li>
              <li>Passport photograph</li>
            </ul>
          </div>

          <div className="requirement-card">
            <h3>Business Loans</h3>
            <ul>
              <li>All individual documents above</li>
              <li>Business registration (CAC certificate)</li>
              <li>Tax clearance certificate</li>
              <li>Business bank statements (6-12 months)</li>
              <li>Business address verification (optional)</li>
              <li>Business plan or financial statements (optional)</li>
            </ul>
          </div>

          <div className="requirement-card">
            <h3>File Requirements</h3>
            <ul>
              <li>Maximum file size: 5MB per document</li>
              <li>Accepted formats: PDF, JPG, PNG</li>
              <li>Documents must be clear and legible</li>
              <li>All information must be visible</li>
              <li>No expired documents</li>
            </ul>
          </div>
        </div>
      </div>

      <style>{`
        /* Tablet Responsive Styles */
        @media (max-width: 1024px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .page-header button {
            width: 100%;
          }

          .documents-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .requirements-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .documents-page {
            padding: 1rem;
          }

          .page-header h1 {
            font-size: 1.75rem;
          }

          .page-header p {
            font-size: 0.9375rem;
          }

          .upload-form-section {
            padding: 1.25rem;
          }

          .account-type-selector {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .radio-group {
            flex-direction: column;
            width: 100%;
          }

          .radio-option {
            width: 100%;
          }

          .documents-list-section h2 {
            font-size: 1.25rem;
          }

          .documents-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .document-card {
            padding: 1.25rem;
          }

          .document-type {
            font-size: 1rem;
          }

          .document-filename {
            font-size: 0.875rem;
          }

          .document-actions {
            flex-wrap: wrap;
            gap: 0.5rem;
          }

          .document-actions button {
            flex: 1 1 auto;
            min-width: 100px;
          }

          .empty-state {
            padding: 2rem 1rem;
          }

          .empty-state h3 {
            font-size: 1.25rem;
          }

          .empty-state p {
            font-size: 0.9375rem;
          }

          .empty-state-icon svg,
          .empty-state svg {
            width: 48px;
            height: 48px;
          }

          .document-requirements-section h2 {
            font-size: 1.25rem;
          }

          .requirements-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .requirement-card {
            padding: 1.25rem;
          }

          .requirement-card h3 {
            font-size: 1.0625rem;
          }

          .requirement-card ul {
            font-size: 0.875rem;
          }
        }

        /* Small Mobile Responsive Styles */
        @media (max-width: 480px) {
          .documents-page {
            padding: 0.875rem;
          }

          .page-header h1 {
            font-size: 1.5rem;
          }

          .upload-form-section {
            padding: 1rem;
          }

          .document-card {
            padding: 1rem;
          }

          .document-header {
            margin-bottom: 0.75rem;
          }

          .document-icon {
            width: 2rem;
            height: 2rem;
          }

          .document-icon svg {
            width: 20px;
            height: 20px;
          }

          .document-actions {
            flex-direction: column;
          }

          .document-actions button {
            width: 100%;
          }

          .empty-state {
            padding: 1.5rem 0.875rem;
          }

          .empty-state svg {
            width: 40px;
            height: 40px;
          }

          .requirement-card {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};
